import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Server, Recycle, Gem, RotateCcw, CheckCircle2, Wrench, ShieldCheck, Truck, TrendingUp } from 'lucide-react';
import { services, secureOperations } from '../data/mock';

const iconMap = { Server, Recycle, Gem, RotateCcw, Wrench, ShieldCheck, Truck, TrendingUp };

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [location]);

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-4">
              Our Services
            </p>
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-6">
              Comprehensive <span className="font-semibold">Circular Solutions</span>
            </h1>
            <p className="text-lg text-gray-400">
              From collection to certification, we provide end-to-end services for e-waste management, IT asset disposal, and circular economy enablement.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Recycle;
              const isEven = index % 2 === 0;
              const images = [
                'https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg',
                'https://images.pexels.com/photos/343457/pexels-photo-343457.jpeg',
                'https://images.pexels.com/photos/1145434/pexels-photo-1145434.jpeg',
                'https://images.pexels.com/photos/209251/pexels-photo-209251.jpeg',
              ];

              return (
                <div key={service.id} id={service.id} className="scroll-mt-28">
                  <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
                    <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                      <div className="flex items-center gap-3 mb-4">
                        <IconComponent className="w-8 h-8 text-[#4fd1c5]" />
                        <span className="text-[#4fd1c5] text-xs font-semibold tracking-[0.15em] uppercase">
                          Service {String(index + 1).padStart(2, '0')}
                        </span>
                      </div>
                      <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-2">
                        <span className="font-semibold">{service.title}</span>
                      </h2>
                      <p className="text-lg text-[#4fd1c5] font-medium mb-4">{service.tagline}</p>
                      <p className="text-gray-600 mb-4">{service.description}</p>
                      {service.subDescription && <p className="text-gray-500 mb-6 text-sm">{service.subDescription}</p>}

                      {service.benefits && (
                        <div className="mb-6">
                          <h4 className="text-sm font-semibold text-[#0a1628] uppercase tracking-wider mb-3">What we deliver</h4>
                          <ul className="space-y-2">
                            {service.benefits.slice(0, 4).map((b, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-[#4fd1c5] mt-1 flex-shrink-0" />
                                <span className="text-gray-600 text-sm">{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <Link to="/contact" className="inline-flex items-center gap-2 bg-[#0a1628] hover:bg-[#4fd1c5] text-white hover:text-[#0a1628] px-6 py-3 font-semibold transition-colors">
                        Get Started
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                    <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                      <img 
                        src={images[index % 4]} 
                        alt={service.title}
                        className="w-full aspect-video object-cover grayscale hover:grayscale-0 transition-all duration-500"
                      />
                    </div>
                  </div>
                  {index < services.length - 1 && <div className="border-t border-gray-100 mt-20" />}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Secure Operations */}
      <section className="py-16 lg:py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              Secure Operations
            </p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628]">
              Service <span className="font-semibold">Modules</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {secureOperations.map((op) => {
              const IconComponent = iconMap[op.icon] || Wrench;
              return (
                <div key={op.id} className="p-6 bg-white border border-gray-200 hover:border-[#4fd1c5] transition-colors">
                  <IconComponent className="w-8 h-8 text-[#4fd1c5] mb-4" />
                  <h3 className="font-semibold text-[#0a1628] mb-2">{op.title}</h3>
                  <p className="text-gray-500 text-sm">{op.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-white mb-6">
            Ready to Get <span className="font-semibold">Started?</span>
          </h2>
          <p className="text-gray-400 mb-8">Contact us today for a customized solution.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#4fd1c5] hover:bg-[#38b2ac] text-[#0a1628] px-8 py-4 font-semibold transition-colors">
              Request a Pickup <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/contact#partner" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white px-8 py-4 font-semibold transition-colors">
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
